import type { CardDensity } from '@troia-v3/types';

/**
 * Tokens de espaço/medida por densidade do card de kanban.
 *
 * `estimateSize` é a altura estimada da LINHA VIRTUAL — card + passo (`gap`) —,
 * porque o elemento medido por `measureElement` é o wrapper da linha, que carrega
 * o `padding-bottom` do passo. Trocar isso por "altura só do card" desalinha a
 * estimativa da medida real e a coluna converge com barra de rolagem errada.
 *
 * `footerEstimate` é a linha-sentinela de paginação (load-more / "Carregando…" /
 * "Tentar novamente"), que mede ~40px e NÃO acompanha a densidade do card.
 *
 * `overscan` é inversamente proporcional à altura: quanto menor o card, mais
 * linhas cabem na janela e mais linhas precisam ser preparadas fora dela.
 */
export interface DensityTokens {
  /** Altura estimada da linha virtual de card, já incluindo o passo (px). */
  estimateSize: number;
  /** Altura estimada da linha-sentinela do rodapé de paginação (px). */
  footerEstimate: number;
  /** Linhas renderizadas fora da janela visível, para cada lado. */
  overscan: number;
  /** Passo entre cards (px) — publicado como `--kanban-card-gap` pela coluna. */
  gap: number;
}

/**
 * Alturas REAIS medidas depois que os cards das três densidades existiram
 * (calibração de 27/07/2026 — as estimativas originais foram escritas antes,
 * dimensionadas pela linha única do CRM, e subestimavam as três).
 *
 * | densidade | Atendimento | Helpdesk | CRM  | estimativa |
 * |-----------|-------------|----------|------|------------|
 * | detailed  | 268–287     | ~224     | —    | 240        |
 * | normal    | 173–181     | ~127     | ~98  | 140        |
 * | compact   | ~105        | ~80      | ~50  | 80         |
 *
 * Calibração de 15/08/2026: o Helpdesk ganhou o corpo 'normal' (mockup 07
 * "Relógio de etapa"), que fecha a coluna vazia da tabela acima. O ponto médio
 * entre os três módulos não se moveu o bastante para justificar mexer no token.
 *
 * O token é GLOBAL de propósito (decisão D12 do plano): é só a estimativa do
 * PRIMEIRO quadro, e o virtualizador remede cada linha na montagem via
 * `measureElement`. Por isso o valor é o ponto médio entre módulos, e não o
 * valor de nenhum deles — errar para menos alonga a barra de rolagem no
 * primeiro quadro, errar para mais a encurta, e os dois se corrigem na
 * primeira pintura.
 *
 * Quem for afinar depois: NÃO transformar isto em token por módulo sem revisitar
 * a D12 — a alternativa barata é o board passar um override, não o token
 * ramificar.
 */
export const DENSITY_TOKENS: Record<CardDensity, DensityTokens> = {
  detailed: { estimateSize: 240, footerEstimate: 40, overscan: 4, gap: 10 },
  normal: { estimateSize: 140, footerEstimate: 40, overscan: 8, gap: 8 },
  compact: { estimateSize: 80, footerEstimate: 40, overscan: 12, gap: 6 },
};

/** Densidade assumida fora do provedor e quando o board não recebe a prop. */
export const DEFAULT_CARD_DENSITY: CardDensity = 'normal';
